import React, { useMemo, useState } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import { Language } from '../types';
import { Clock, Info, ShieldAlert, Award, Calendar, Activity } from 'lucide-react';
import { triggerHaptic } from '../lib/haptic';

interface MealTimeClusteringProps {
  history?: any[];
  lang: Language;
  theme: 'dark' | 'light';
  skin?: string;
  illumination?: boolean;
}

const translations = {
  en: {
    title: 'Meal Time Clustering',
    subtitle: 'Eating Consistency Scan',
    xAxis: 'Day of Week',
    yAxis: 'Time of Day',
    breakfast: 'Breakfast',
    lunch: 'Lunch',
    dinner: 'Dinner',
    snack: 'Snack',
    consistency: 'Consistency Score',
    highConsistency: 'Highly Consistent',
    modConsistency: 'Moderate Variation',
    lowConsistency: 'High Variation',
    avgTime: 'Avg Time',
    noData: 'Log meals throughout the week to analyze your eating schedule and biological rhythm consistency.',
    targetWindows: 'Ideal Windows',
    recommendation: 'Recommendation',
    recConsistent: 'Your meal schedule shows great rhythm! Keeping consistent times stabilizes blood sugar and enhances digestive efficiency.',
    recVariable: 'Your meal times fluctuate. Try to lock in a ±1 hour window for major meals to improve metabolic alignment and circadian rhythm.',
    mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
    demoNotice: 'Showing Baseline Target Clusters (Demo)',
    idealRange: 'Ideal Window',
    loggedAt: 'Logged at',
    estimated: 'Estimated',
    consistencyStatus: 'Consistency Status',
  },
  ua: {
    title: 'Кластеризація часу прийомів',
    subtitle: 'Аналіз стабільності харчування',
    xAxis: 'День тижня',
    yAxis: 'Час доби',
    breakfast: 'Сніданок',
    lunch: 'Обід',
    dinner: 'Вечеря',
    snack: 'Перекус',
    consistency: 'Індекс стабільності',
    highConsistency: 'Висока стабільність',
    modConsistency: 'Помірна варіативність',
    lowConsistency: 'Висока варіативність',
    avgTime: 'Сер. час',
    noData: 'Записуйте прийоми їжі протягом тижня для аналізу стабільності вашого харчового біоритму.',
    targetWindows: 'Ідеальні вікна',
    recommendation: 'Рекомендація',
    recConsistent: 'Ваш розклад демонструє чудовий біоритм! Стабільний час стабілізує рівень цукру в крові та оптимізує травлення.',
    recVariable: 'Час прийомів їжі коливається. Спробуйте встановити інтервал ±1 год для основних страв для кращої метаболічної синхронізації.',
    mon: 'Пн', tue: 'Вт', wed: 'Ср', thu: 'Чт', fri: 'Пт', sat: 'Сб', sun: 'Нд',
    demoNotice: 'Показано базові цільові кластери (Демо)',
    idealRange: 'Ідеальний інтервал',
    loggedAt: 'Записано о',
    estimated: 'Оцінено',
    consistencyStatus: 'Статус стабільності',
  }
};

const getDayLabel = (dayIdx: number, isUa: boolean) => {
  const days = isUa 
    ? ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return days[dayIdx] || '';
};

export const MealTimeClustering: React.FC<MealTimeClusteringProps> = ({
  history = [],
  lang,
  theme,
  skin = 'default',
  illumination = false
}) => {
  const isStalker = skin === 'stalker';
  const isUa = lang === 'ua';
  const t = isUa ? translations.ua : translations.en;
  
  const [showIdealWindows, setShowIdealWindows] = useState(true);

  // Parse actual meals or provide high-quality baseline demonstration clusters
  const { parsedMeals, isUsingDemo } = useMemo(() => {
    // Only count history entries with foods to filter out water logs or empty records
    let processed = (history || [])
      .filter(e => e.foods && e.foods.length > 0)
      .map(entry => {
        const date = new Date(entry.timestamp);
        const day = (date.getDay() + 6) % 7; // Mon is 0, Sun is 6
        const hour = date.getHours() + date.getMinutes() / 60;
        
        let type = entry.meal_type || 'snack';
        if (!entry.meal_type || entry.meal_type === 'snack') {
          const h = date.getHours();
          if (h >= 5 && h < 11) type = 'breakfast';
          else if (h >= 11 && h < 16) type = 'lunch';
          else if (h >= 17 && h < 22) type = 'dinner';
        }
        
        return {
          day,
          hour,
          calories: entry.calories_kcal || entry.calories || 350,
          type,
          meal_name: entry.meal_name || (isUa ? 'Прийом їжі' : 'Meal'),
          timeStr: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
          dayName: getDayLabel(day, isUa),
          isDemo: false
        };
      });

    const isDemo = processed.length === 0;

    if (isDemo) {
      // Generate realistic baseline target clusters across the week
      const demoMeals: any[] = [];
      const mealProfiles = [
        { 
          type: 'breakfast', 
          baseHour: 8.2, 
          variance: 0.3, 
          cal: 420, 
          names: isUa 
            ? ['Вівсянка з фруктами', 'Омлет з сиром', 'Протеїновий шейк', 'Тости з авокадо'] 
            : ['Fruit Oatmeal', 'Cheese Omelette', 'Protein Shake', 'Avocado Toast'] 
        },
        { 
          type: 'lunch', 
          baseHour: 13.2, 
          variance: 0.45, 
          cal: 680, 
          names: isUa 
            ? ['Куряча грудка з кіноа', 'Салат з тунцем', 'Паста з томатами', 'Грибний крем-суп'] 
            : ['Chicken & Quinoa', 'Tuna Salad', 'Tomato Pasta', 'Mushroom Cream Soup'] 
        },
        { 
          type: 'dinner', 
          baseHour: 19.3, 
          variance: 0.6, 
          cal: 510, 
          names: isUa 
            ? ['Запечений лосось', 'Овочеве рагу', 'Стейк з індички', 'Легкий салат'] 
            : ['Baked Salmon', 'Vegetable Stew', 'Turkey Steak', 'Light Green Salad'] 
        }
      ];

      for (let day = 0; day < 7; day++) {
        mealProfiles.forEach(p => {
          // Occasionally skip or slide meal times to look organic
          if (Math.random() > 0.12) {
            const hRandom = p.baseHour + (Math.random() * 2 - 1) * p.variance;
            const hour = Math.min(23.9, Math.max(0, hRandom));
            const randomMin = Math.floor((hour % 1) * 60);
            const randomHour = Math.floor(hour);
            const timeStr = `${String(randomHour).padStart(2, '0')}:${String(randomMin).padStart(2, '0')}`;
            const name = p.names[Math.floor(Math.random() * p.names.length)];
            demoMeals.push({
              day,
              hour,
              calories: Math.round(p.cal + (Math.random() * 120 - 60)),
              type: p.type,
              meal_name: name,
              timeStr,
              dayName: getDayLabel(day, isUa),
              isDemo: true
            });
          }
        });
      }
      return { parsedMeals: demoMeals, isUsingDemo: true };
    }

    return { parsedMeals: processed, isUsingDemo: false };
  }, [history, isUa]);

  // Calculate consistency parameters and average logging hours
  const mealStats = useMemo(() => {
    const stats: Record<string, { avgHour: number; stdDev: number; count: number; timeStr: string; label: string; status: string; statusColor: string }> = {
      breakfast: { avgHour: 0, stdDev: 0, count: 0, timeStr: '--:--', label: t.breakfast, status: t.lowConsistency, statusColor: 'text-red-500' },
      lunch: { avgHour: 0, stdDev: 0, count: 0, timeStr: '--:--', label: t.lunch, status: t.lowConsistency, statusColor: 'text-red-500' },
      dinner: { avgHour: 0, stdDev: 0, count: 0, timeStr: '--:--', label: t.dinner, status: t.lowConsistency, statusColor: 'text-red-500' }
    };

    ['breakfast', 'lunch', 'dinner'].forEach(type => {
      const meals = parsedMeals.filter(m => m.type === type);
      if (meals.length === 0) return;

      const hours = meals.map(m => m.hour);
      const sum = hours.reduce((acc, h) => acc + h, 0);
      const avg = sum / hours.length;

      // StdDev calculation
      const sqDiffs = hours.map(h => Math.pow(h - avg, 2));
      const avgSqDiff = sqDiffs.reduce((acc, d) => acc + d, 0) / hours.length;
      const stdDev = Math.sqrt(avgSqDiff);

      // Hour format converter
      const avgHourInt = Math.floor(avg);
      const avgMinInt = Math.floor((avg % 1) * 60);
      const timeStr = `${String(avgHourInt).padStart(2, '0')}:${String(avgMinInt).padStart(2, '0')}`;

      // Status grading based on stdDev (in hours)
      let status = t.highConsistency;
      let statusColor = 'text-emerald-500 dark:text-emerald-400';
      if (stdDev > 1.5) {
        status = t.lowConsistency;
        statusColor = 'text-rose-500 dark:text-rose-400';
      } else if (stdDev > 0.75) {
        status = t.modConsistency;
        statusColor = 'text-amber-500 dark:text-amber-400';
      }

      stats[type] = {
        avgHour: avg,
        stdDev,
        count: meals.length,
        timeStr,
        label: type === 'breakfast' ? t.breakfast : type === 'lunch' ? t.lunch : t.dinner,
        status,
        statusColor
      };
    });

    return stats;
  }, [parsedMeals, t]);

  // Overall rhythm stability score (percentage 0-100%)
  const rhythmScore = useMemo(() => {
    const validStats = Object.values(mealStats).filter(s => s.count > 0);
    if (validStats.length === 0) return 100;
    
    // Average standard deviation across main meals
    const avgStdDev = validStats.reduce((sum, s) => sum + s.stdDev, 0) / validStats.length;
    // Map standard deviation to a percentage (0.2h stddev = ~100%, 3h stddev = ~30%)
    const score = Math.max(10, Math.min(100, Math.round(100 - (avgStdDev * 25))));
    return score;
  }, [mealStats]);

  // Format Y Axis ticks
  const formatYAxis = (value: number) => {
    const h = Math.floor(value);
    return `${String(h).padStart(2, '0')}:00`;
  };

  // Split parsed meals into breakfast, lunch, and dinner arrays for separate scatter plots
  const breakfasts = useMemo(() => parsedMeals.filter(m => m.type === 'breakfast'), [parsedMeals]);
  const lunches = useMemo(() => parsedMeals.filter(m => m.type === 'lunch'), [parsedMeals]);
  const dinners = useMemo(() => parsedMeals.filter(m => m.type === 'dinner'), [parsedMeals]);
  const snacks = useMemo(() => parsedMeals.filter(m => m.type === 'snack'), [parsedMeals]);

  // Custom tooltips
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className={cn(
          "p-3 rounded-xl border font-sans text-xs shadow-xl backdrop-blur-md",
          isStalker 
            ? "bg-black/95 border-[#4ade80] text-[#4ade80] font-mono" 
            : theme === 'dark' 
              ? "bg-zinc-950/90 border-zinc-800 text-zinc-100 shadow-black/80" 
              : "bg-white/95 border-zinc-100 text-zinc-800"
        )}>
          <div className="font-bold flex items-center gap-1.5 mb-1 text-sm">
            <Clock className="w-3.5 h-3.5 text-emerald-500" />
            <span>{data.meal_name}</span>
          </div>
          <div className="space-y-1 mt-1.5 opacity-90 border-t border-dashed border-current/20 pt-1.5">
            <div>
              <span className="opacity-60">{t.xAxis}:</span> <span className="font-semibold">{data.dayName}</span>
            </div>
            <div>
              <span className="opacity-60">{t.loggedAt}:</span> <span className="font-semibold">{data.timeStr}</span>
            </div>
            <div>
              <span className="opacity-60">{isStalker ? "CALORIES" : "Calories"}:</span> <span className="font-bold text-emerald-500">{data.calories} kcal</span>
            </div>
            {data.isDemo && (
              <div className="text-[10px] text-amber-500 font-bold uppercase tracking-widest mt-1">
                ⚠️ {t.estimated}
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  if (isStalker) {
    return (
      <motion.div 
        id="stalker-meal-clustering"
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="stalker-panel p-4 text-[#4ade80] font-mono bg-black/85 border border-[#4ade80]/30 rounded space-y-4"
      >
        <style>{`
          .crt-flicker {
            animation: crt-glow 0.15s infinite;
          }
          @keyframes crt-glow {
            0% { opacity: 0.98; }
            50% { opacity: 1; }
            100% { opacity: 0.99; }
          }
        `}</style>

        {/* Panel Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-[#4ade80]/30 pb-2 border-dashed gap-2">
          <div className="space-y-0.5">
            <h3 className="text-lg stalker-font text-glow text-[#4ade80] uppercase flex items-center gap-2">
              <Activity className="w-4 h-4 animate-pulse text-[#4ade80]" /> MEAL CHRONO-CLUSTERING MATRIX
            </h3>
            <p className="text-[10px] text-[#4ade80]/60 uppercase tracking-widest">
              DIAGONAL TEMPORAL CIRCADIAN SCAN
            </p>
          </div>
          
          <button
            onClick={() => {
              setShowIdealWindows(!showIdealWindows);
              triggerHaptic('SUCCESS');
            }}
            className="px-2 py-1 text-[10px] border border-[#4ade80]/50 hover:bg-[#4ade80]/10 rounded uppercase stalker-font transition-colors text-center shrink-0"
          >
            {showIdealWindows ? "DISABLE IDEAL WINDOWS" : "ENABLE IDEAL WINDOWS"}
          </button>
        </div>

        {/* Demo Notification */}
        {isUsingDemo && (
          <div className="border border-amber-500/30 bg-amber-950/20 px-3 py-1.5 text-xs text-amber-500 flex items-center gap-2 uppercase stalker-font">
            <Info className="w-3.5 h-3.5" />
            <span>{t.demoNotice}</span>
          </div>
        )}

        {/* Scatter Chart */}
        <div className="h-[220px] w-full relative border border-[#4ade80]/20 bg-black/60 p-2 crt-flicker">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 10, left: -25, bottom: -10 }}>
              <XAxis 
                type="number" 
                dataKey="day" 
                domain={[0, 6]}
                tickCount={7}
                axisLine={false}
                tickLine={false}
                tickFormatter={(val) => getDayLabel(val, isUa)}
                tick={{ fill: '#4ade80', fontSize: 11, fontFamily: 'VT323' }}
              />
              <YAxis 
                type="number" 
                dataKey="hour" 
                domain={[5, 23.5]}
                tickCount={7}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatYAxis}
                tick={{ fill: '#4ade80', fontSize: 11, fontFamily: 'VT323' }}
              />
              <ZAxis type="number" dataKey="calories" range={[30, 220]} />
              <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3', stroke: '#4ade80', strokeOpacity: 0.3 }} />
              
              {/* Reference Shaded Areas for Healthy Consistency Windows */}
              {showIdealWindows && (
                <>
                  {/* Breakfast Zone */}
                  <ReferenceArea y1={7} y2={9.5} fill="#4ade80" fillOpacity={0.04} stroke="#4ade80" strokeOpacity={0.08} strokeDasharray="3 3" />
                  {/* Lunch Zone */}
                  <ReferenceArea y1={12} y2={14.5} fill="#4ade80" fillOpacity={0.04} stroke="#4ade80" strokeOpacity={0.08} strokeDasharray="3 3" />
                  {/* Dinner Zone */}
                  <ReferenceArea y1={18} y2={20.5} fill="#4ade80" fillOpacity={0.04} stroke="#4ade80" strokeOpacity={0.08} strokeDasharray="3 3" />
                </>
              )}

              {/* Breakfast Scatter */}
              <Scatter name={t.breakfast} data={breakfasts} fill="#4ade80" shape="circle" isAnimationActive={true} animationDuration={1000} />
              {/* Lunch Scatter */}
              <Scatter name={t.lunch} data={lunches} fill="#f97316" shape="triangle" isAnimationActive={true} animationDuration={1000} />
              {/* Dinner Scatter */}
              <Scatter name={t.dinner} data={dinners} fill="#ef4444" shape="square" isAnimationActive={true} animationDuration={1000} />
              {/* Snack Scatter */}
              <Scatter name={t.snack} data={snacks} fill="#a855f7" shape="cross" isAnimationActive={true} animationDuration={1000} />
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {/* Bottom Consistency Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {['breakfast', 'lunch', 'dinner'].map((type) => {
            const stat = mealStats[type];
            return (
              <div key={type} className="border border-[#4ade80]/20 bg-black/40 p-2.5 rounded text-xs">
                <div className="flex justify-between items-center text-[#4ade80]/70 uppercase text-[10px] tracking-wider mb-1.5">
                  <span>{stat.label}</span>
                  <span className={cn("font-bold uppercase tracking-widest", stat.statusColor)}>{stat.status}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-glow text-lg font-extrabold">{stat.timeStr}</span>
                  <span className="text-[10px] text-[#4ade80]/50 lowercase">
                    {isUa ? 'дисперсія' : 'variance'}: ±{Math.round(stat.stdDev * 60)}m
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Consistency Index HUD */}
        <div className="border border-[#4ade80]/30 bg-black/60 p-3 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs rounded">
          <div className="space-y-1 text-center sm:text-left">
            <span className="text-[10px] text-[#4ade80]/50 uppercase tracking-widest block leading-none">
              CIRCADIAN HARMONIC RESILIENCE
            </span>
            <span className="text-sm font-bold text-glow uppercase block leading-none text-[#4ade80]">
              {rhythmScore >= 80 ? "METABOLIC SYNC LOCKED ⚡" : rhythmScore >= 50 ? "BIOMETRIC FLUX ACTIVE" : "CRITICAL DISCORDANCE REPORTED"}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="h-8 w-24 bg-[#4ade80]/10 border border-[#4ade80]/30 rounded relative overflow-hidden shrink-0">
              <div 
                className="h-full bg-[#4ade80] shadow-[0_0_8px_#4ade80] transition-all duration-1000" 
                style={{ width: `${rhythmScore}%` }} 
              />
              <span className="absolute inset-0 flex items-center justify-center font-bold text-xs mix-blend-difference text-white">
                {rhythmScore}%
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // DEFAULT / STANDARD PREMIUM CARD VIEW (With beautiful light/dark styles)
  return (
    <motion.div 
      id="meal-time-clustering-card"
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "p-6 rounded-[28px] border transition-all flex flex-col space-y-4",
        theme === 'light'
          ? "bg-white border-zinc-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          : "bg-zinc-900 border-zinc-800 card-shadow",
        illumination ? "illumination-food" : ""
      )}
    >
      {/* Card Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 select-none">
        <div className="space-y-1 text-left">
          <h3 className="text-md font-black uppercase tracking-wider text-zinc-800 dark:text-zinc-200">
            {t.title}
          </h3>
          <p className="text-[11px] text-zinc-500 font-bold uppercase tracking-widest flex items-center gap-1">
            <Activity className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            {t.subtitle}
          </p>
        </div>

        <button
          onClick={() => {
            setShowIdealWindows(!showIdealWindows);
            triggerHaptic('SUCCESS');
          }}
          className={cn(
            "px-3 py-1.5 rounded-xl text-[10px] font-extrabold uppercase tracking-widest border transition-all cursor-pointer self-start sm:self-center shrink-0",
            showIdealWindows
              ? "bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-500/20"
              : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border-zinc-200 dark:border-zinc-700"
          )}
        >
          {showIdealWindows ? t.idealRange : (isUa ? 'Показати вікна' : 'Show Windows')}
        </button>
      </div>

      {/* Demo Notification Banner */}
      {isUsingDemo && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl px-4 py-2 text-xs text-amber-600 dark:text-amber-400 font-medium flex items-center gap-2">
          <Info className="w-4 h-4 text-amber-500" />
          <span>{t.demoNotice}</span>
        </div>
      )}

      {/* Scatter Chart View */}
      <div className="h-[220px] w-full relative">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 10, right: 10, left: -25, bottom: -10 }}>
            <XAxis 
              type="number" 
              dataKey="day" 
              domain={[0, 6]}
              tickCount={7}
              axisLine={false}
              tickLine={false}
              tickFormatter={(val) => getDayLabel(val, isUa)}
              tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa', fontWeight: 'bold' }}
            />
            <YAxis 
              type="number" 
              dataKey="hour" 
              domain={[5, 23.5]}
              tickCount={7}
              axisLine={false}
              tickLine={false}
              tickFormatter={formatYAxis}
              tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa', fontWeight: 'bold' }}
            />
            <ZAxis type="number" dataKey="calories" range={[40, 240]} />
            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3', stroke: theme === 'dark' ? '#3f3f46' : '#e4e4e7' }} />
            
            {/* Shaded ideal zones for digestion mealtimes */}
            {showIdealWindows && (
              <>
                <ReferenceArea y1={7} y2={9.5} fill="#10b981" fillOpacity={theme === 'dark' ? 0.04 : 0.03} stroke="#10b981" strokeOpacity={0.08} strokeDasharray="4 4" />
                <ReferenceArea y1={12} y2={14.5} fill="#f59e0b" fillOpacity={theme === 'dark' ? 0.04 : 0.03} stroke="#f59e0b" strokeOpacity={0.08} strokeDasharray="4 4" />
                <ReferenceArea y1={18} y2={20.5} fill="#ef4444" fillOpacity={theme === 'dark' ? 0.04 : 0.03} stroke="#ef4444" strokeOpacity={0.08} strokeDasharray="4 4" />
              </>
            )}

            {/* Scatter datasets for separate visual categories */}
            <Scatter name={t.breakfast} data={breakfasts} fill="#10b981" shape="circle" isAnimationActive={true} animationDuration={1100} />
            <Scatter name={t.lunch} data={lunches} fill="#f59e0b" shape="triangle" isAnimationActive={true} animationDuration={1100} />
            <Scatter name={t.dinner} data={dinners} fill="#ef4444" shape="square" isAnimationActive={true} animationDuration={1100} />
            <Scatter name={t.snack} data={snacks} fill="#8b5cf6" shape="cross" isAnimationActive={true} animationDuration={1100} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Meal type stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
        {['breakfast', 'lunch', 'dinner'].map((type) => {
          const stat = mealStats[type];
          return (
            <div 
              key={type} 
              className={cn(
                "p-3.5 rounded-2xl border text-left flex flex-col space-y-1.5",
                theme === 'light' 
                  ? "bg-zinc-50 border-zinc-100" 
                  : "bg-zinc-900/60 border-zinc-800/80"
              )}
            >
              <div className="flex justify-between items-center text-[10px] font-extrabold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                <span>{stat.label}</span>
                <span className={cn("font-black text-[9px] uppercase tracking-widest", stat.statusColor)}>
                  {stat.status}
                </span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-md font-black text-zinc-800 dark:text-zinc-100">{stat.timeStr}</span>
                <span className="text-[10px] text-zinc-400 font-semibold font-mono">
                  ±{Math.round(stat.stdDev * 60)}m
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Consistency HUD bar */}
      <div className={cn(
        "rounded-2xl p-4 border flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-sans",
        theme === 'light'
          ? "bg-emerald-500/5 border-emerald-500/10 text-emerald-900"
          : "bg-emerald-500/10 border-emerald-500/20 text-emerald-200"
      )}>
        <div className="space-y-1 text-center sm:text-left flex-1">
          <span className="text-[10px] uppercase font-extrabold tracking-wider opacity-60 block leading-none mb-1">
            {t.consistencyStatus}
          </span>
          <span className="font-extrabold text-xs block leading-tight text-emerald-600 dark:text-emerald-400 uppercase">
            {rhythmScore >= 80 
              ? (isUa ? "Синхронізація біоритму зафіксована! ✨" : "Circadian Sync Locked! ✨") 
              : rhythmScore >= 55 
                ? (isUa ? "Біоритм в межах норми" : "Biological Rhythm Active") 
                : (isUa ? "Рекомендується покращити стабільність" : "Circadian Realignment Advised")}
          </span>
          <p className="text-[10px] leading-relaxed opacity-75 font-medium pt-1">
            {rhythmScore >= 70 ? t.recConsistent : t.recVariable}
          </p>
        </div>

        {/* Dynamic circular progress representing rhythmScore */}
        <div className="flex items-center gap-3 shrink-0 self-center">
          <div className="relative w-12 h-12 flex items-center justify-center shrink-0">
            <svg className="w-12 h-12 transform -rotate-90">
              <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="4" className="text-zinc-200 dark:text-zinc-800/80" fill="transparent" />
              <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="4" className="text-emerald-500" strokeDasharray={2 * Math.PI * 20} strokeDasharray-offset={2 * Math.PI * 20} strokeDashoffset={2 * Math.PI * 20 * (1 - rhythmScore / 100)} fill="transparent" />
            </svg>
            <span className="absolute font-black text-[11px] text-zinc-800 dark:text-zinc-100">
              {rhythmScore}%
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
